import React from "react";
import { useParams, Link } from "react-router-dom";

import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import Grid, { GridColumn } from "ui/Grid";
import Loading from "ui/Loading";
import { CHECK_INFO } from "features/Checks/CheckList";
import { useRoutes } from "utils/routes";
import { useCurrentOrganization } from "providers/WithCurrentOrganization";

import {
  useAlertConfigDetailsQuery,
  AlertConfigDetailsQuery,
} from "features/Checks/AlertConfig/gql/Query.generated";

type CheckConfig = AlertConfigDetailsQuery["getAlertCheckConfigs"][number];
type Rule = CheckConfig["rules"][number];
type Row = { check: string; title: string; titleDisplay: React.ReactNode };

function AlertConfig() {
  const { serverId } = useParams();
  const { slug: organizationSlug } = useCurrentOrganization();
  const {
    organizationAlerts,
    organizationAlertRules,
    organizationCheckConfigure,
    serverCheckConfigure,
  } = useRoutes();

  const { data, loading, error } = useAlertConfigDetailsQuery({
    variables: { organizationSlug, serverId: serverId ?? null },
  });

  if (loading || error || !data) {
    return <Loading error={!!error} />;
  }

  const isServerScope = !!serverId;
  const configByCheck = new Map<string, CheckConfig>(
    data.getAlertCheckConfigs.map((c) => [c.checkKind, c]),
  );

  const tableData: Row[] = Object.entries(CHECK_INFO).map(([check, info]) => ({
    check,
    title: info.title,
    titleDisplay: info.titleDisplay,
  }));

  const columns: GridColumn<Row, keyof Row>[] = [
    {
      field: "title",
      header: "Check",
      width: "minmax(220px,1fr)",
      renderer: ({ rowData }) => (
        <Link
          to={
            serverId
              ? serverCheckConfigure(serverId, rowData.check)
              : organizationCheckConfigure(organizationSlug, rowData.check)
          }
        >
          {rowData.titleDisplay}
        </Link>
      ),
    },
    {
      field: "check",
      header: "Alert rules",
      width: "minmax(340px,2fr)",
      disableSort: true,
      renderer: ({ rowData }) => (
        <RulesCell
          config={configByCheck.get(rowData.check)}
          isServerScope={isServerScope}
          organizationSlug={organizationSlug}
        />
      ),
    },
  ];

  return (
    <Panel
      title="Check Alert Rules"
      secondaryTitle={
        serverId ? (
          <Link to={organizationAlerts(organizationSlug)}>
            View organization-wide
          </Link>
        ) : undefined
      }
    >
      <PanelSection>
        The alert rules that apply to each check
        {serverId
          ? " on this server"
          : " across the organization. Select a server to see rules targeted to that server"}
        . Edit a rule by selecting it, or manage all rules on the{" "}
        <Link to={organizationAlertRules(organizationSlug)}>Alert Rules</Link>{" "}
        tab.
      </PanelSection>
      <Grid data={tableData} columns={columns} defaultSortBy="title" striped />
    </Panel>
  );
}

// The rules that apply to a check, as middle-dot-separated name links (to each
// rule's editor), sorted alphabetically.
function RuleLinks({
  rules,
  organizationSlug,
}: {
  rules: Rule[];
  organizationSlug: string;
}) {
  const { organizationAlertRule } = useRoutes();
  return (
    <>
      {[...rules]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((rule, i) => (
          <React.Fragment key={rule.id}>
            {i > 0 && " · "}
            <Link to={organizationAlertRule(organizationSlug, rule.id)}>
              {rule.name}
            </Link>
          </React.Fragment>
        ))}
    </>
  );
}

function RulesCell({
  config,
  isServerScope,
  organizationSlug,
}: {
  config?: CheckConfig;
  isServerScope: boolean;
  organizationSlug: string;
}) {
  const scoped = config?.rules ?? [];
  // Only server scope inherits org-wide rules; org scope shows its own.
  const inherited = isServerScope ? config?.inheritedRules ?? [] : [];

  if (scoped.length === 0 && inherited.length === 0) {
    return (
      <span className="text-[#606771]">
        {isServerScope ? "No rules" : "No organization-wide rules"}
      </span>
    );
  }

  // Render inline (no wrapper padding / font-size) so the rule text lines up with
  // the check name in the adjacent column and rows stay a uniform height.
  return (
    <>
      <RuleLinks rules={scoped} organizationSlug={organizationSlug} />
      {inherited.length > 0 && (
        <span className="text-[#606771]">
          {scoped.length > 0 ? " " : ""}(inherited from organization:{" "}
          <RuleLinks rules={inherited} organizationSlug={organizationSlug} />)
        </span>
      )}
    </>
  );
}

export default AlertConfig;
